/**
 * IT Admin → Management → Teams bot (19 Oct): is the IT HelpDesk bot working?
 *
 * Status (switched on, Bot ID set, secret set yes/no — never the value — and
 * the endpoint the Developer Portal must point at), how many people have the
 * app, which channel gets P1 cards (one the bot was added to; none = the
 * webhook), which channel gets Backoffice's "new user ready" cards (21 Oct;
 * none = ACCESS_BACKOFFICE_WEBHOOK_URL), and a test card to oneself. Admins only (`<AdminRoute>`; every
 * `/admin/teams-bot` endpoint checks again).
 */

import { Alert, Badge, Button, Field, Typography } from "@norican/react";
import { useState } from "react";

import { ErrorState, LoadingState, PageContainer, SectionCard } from "@/components/common";
import { OptionSelect } from "@/features/knowledge/shared";
import { useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import type { TeamsBotChannel, TeamsBotPerson } from "@/lib/api";

import {
  useSendTestCard,
  useSetBackofficeChannel,
  useSetP1Channel,
  useTeamsBotStatus,
} from "./api";

function channelLabel(channel: TeamsBotChannel): string {
  return (
    [channel.team_name, channel.channel_name].filter(Boolean).join(" › ") || channel.conversation_id
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b py-2 last:border-b-0">
      <Typography variant="body-sm" tone="muted">
        {label}
      </Typography>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

function YesNo({ value }: { value: boolean }) {
  const { t } = useT();
  return (
    <Badge size="sm" tone={value ? "success" : "neutral"}>
      {value ? t("teamsBot.yes") : t("teamsBot.no")}
    </Badge>
  );
}

export function TeamsBotPage() {
  const { t } = useT();
  useDocumentTitle(t("teamsBot.title"));
  const status = useTeamsBotStatus();
  const setP1 = useSetP1Channel();
  const setBackoffice = useSetBackofficeChannel();
  const test = useSendTestCard();

  if (status.isPending) {
    return (
      <PageContainer title={t("teamsBot.title")}>
        <LoadingState />
      </PageContainer>
    );
  }
  if (status.isError) {
    return (
      <PageContainer title={t("teamsBot.title")}>
        <ErrorState error={status.error} onRetry={() => void status.refetch()} />
      </PageContainer>
    );
  }
  const s = status.data;
  const channels = s.channels ?? [];
  return (
    <PageContainer title={t("teamsBot.title")}>
      <div className="flex flex-col gap-4">
        {s.active ? null : <Alert tone="warning">{t("teamsBot.inactive")}</Alert>}
        <SectionCard title={t("teamsBot.status.title")}>
          <Row label={t("teamsBot.status.enabled")}>
            <YesNo value={s.enabled} />
          </Row>
          <Row label={t("teamsBot.status.appId")}>
            {s.app_id ? (
              <Typography variant="body-sm" className="font-mono">
                {s.app_id}
              </Typography>
            ) : (
              <YesNo value={false} />
            )}
          </Row>
          <Row label={t("teamsBot.status.secret")}>
            <YesNo value={s.secret_set} />
          </Row>
          <Row label={t("teamsBot.status.endpoint")}>
            <Typography variant="body-sm" className="font-mono break-all">
              {s.messaging_endpoint}
            </Typography>
          </Row>
          <Row label={t("teamsBot.status.installed")}>
            <Typography variant="label">{s.installed_users}</Typography>
          </Row>
        </SectionCard>

        <PeopleSection people={s.people ?? []} />

        <ChannelSection
          name="teams-bot-p1"
          keyPrefix="p1"
          channels={channels}
          current={s.p1_channel?.conversation_id ?? ""}
          viaBot={s.p1_via_bot ?? false}
          mutation={setP1}
        />
        <ChannelSection
          name="teams-bot-backoffice"
          keyPrefix="backoffice"
          channels={channels}
          current={s.backoffice_channel?.conversation_id ?? ""}
          viaBot={s.backoffice_via_bot ?? false}
          webhookSet={s.backoffice_webhook_set ?? false}
          mutation={setBackoffice}
        />

        <SectionCard title={t("teamsBot.test.title")} description={t("teamsBot.test.intro")}>
          <div className="flex flex-col gap-3">
            <div>
              <Button size="sm" disabled={test.isPending} onClick={() => test.mutate()}>
                {t("teamsBot.test.send")}
              </Button>
            </div>
            {test.data ? (
              <Alert tone={test.data.sent ? "success" : "warning"}>
                {test.data.sent
                  ? t("teamsBot.test.sent")
                  : (test.data.reason ?? t("teamsBot.test.notSent"))}
              </Alert>
            ) : null}
            {test.isError ? <ErrorState variant="inline" error={test.error} /> : null}
          </div>
        </SectionCard>
      </div>
    </PageContainer>
  );
}

/** 23 Oct: who the bot can reach (Teams installed the app for them, e.g. by setup policy). */
function PeopleSection({ people }: { people: TeamsBotPerson[] }) {
  const { t } = useT();
  const { locale } = useLanguage();
  const when = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short" }) : "–";
  return (
    <SectionCard title={t("teamsBot.people.title")} description={t("teamsBot.people.intro")}>
      {people.length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {t("teamsBot.people.none")}
        </Typography>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-border border-b">
                <th scope="col" className="py-2 text-left">
                  {t("teamsBot.people.name")}
                </th>
                <th scope="col" className="py-2 text-left">
                  {t("teamsBot.people.email")}
                </th>
                <th scope="col" className="py-2 text-left">
                  {t("teamsBot.people.updated")}
                </th>
              </tr>
            </thead>
            <tbody>
              {people.map((p, i) => (
                <tr key={`${p.email ?? ""}-${i}`} className="border-border-subtle border-b">
                  <td className="py-1.5">
                    <span className="flex items-center gap-2">
                      {p.name ?? "–"}
                      {p.installed ? null : (
                        <Badge size="sm" tone="neutral">
                          {t("teamsBot.people.removed")}
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="py-1.5 break-all">
                    {p.email ?? (
                      <Badge size="sm" tone="warning">
                        {t("teamsBot.people.noEmail")}
                      </Badge>
                    )}
                  </td>
                  <td className="py-1.5">{when(p.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/** One "which channel gets these cards" choice: P1 cards, Backoffice's new users (21 Oct). */
function ChannelSection({
  name,
  keyPrefix,
  channels,
  current,
  viaBot,
  webhookSet,
  mutation,
}: {
  name: string;
  keyPrefix: "p1" | "backoffice";
  channels: TeamsBotChannel[];
  current: string;
  viaBot: boolean;
  webhookSet?: boolean;
  mutation: ReturnType<typeof useSetP1Channel>;
}) {
  const { t } = useT();
  const [picked, setPicked] = useState<string | null>(null);
  const choice = picked ?? current;
  const options = [
    { value: "", label: t(`teamsBot.${keyPrefix}.none`) },
    ...channels
      .filter((c) => c.installed)
      .map((c) => ({ value: c.conversation_id, label: channelLabel(c) })),
  ];
  const via = viaBot
    ? t(`teamsBot.${keyPrefix}.viaBot`)
    : webhookSet === false
      ? t("teamsBot.backoffice.nowhere")
      : t(`teamsBot.${keyPrefix}.viaWebhook`);
  return (
    <SectionCard
      title={t(`teamsBot.${keyPrefix}.title`)}
      description={t(`teamsBot.${keyPrefix}.intro`)}
    >
      <div className="flex flex-col gap-3">
        {channels.length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            {t("teamsBot.p1.noChannels")}
          </Typography>
        ) : (
          <Field name={name} label={t("teamsBot.p1.label")}>
            <OptionSelect
              options={options}
              value={choice}
              onValueChange={setPicked}
              placeholder={t(`teamsBot.${keyPrefix}.none`)}
              aria-label={t(`teamsBot.${keyPrefix}.title`)}
            />
          </Field>
        )}
        <Typography variant="caption" tone="muted">
          {via}
        </Typography>
        {channels.length > 0 ? (
          <div>
            <Button
              size="sm"
              disabled={choice === current || mutation.isPending}
              onClick={() => mutation.mutate(choice === "" ? null : choice)}
            >
              {t("button.save")}
            </Button>
          </div>
        ) : null}
        {mutation.isError ? <ErrorState variant="inline" error={mutation.error} /> : null}
      </div>
    </SectionCard>
  );
}
